import { Component, OnInit, ViewEncapsulation, inject } from "@angular/core";
import { MatTabsModule } from "@angular/material/tabs";
import {
    AssetManagementService,
    AssetProjection,
} from "@app/assetmanagement/services/assetmanagement-data.service";
import { flattenOrgTree, toDisplayValue } from "@methods/CommonMethods";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { CerrixIconButtonComponent } from "@app/shared/atoms/cerrix-icon-button/cerrix-icon-button.component";
import { CerrixPageFooterComponent } from "@app/shared/cerrix-page-footer/cerrix-page-footer.component";
import { CerrixHeaderComponent } from "@app/shared/organisms/cerrix-header/cerrix-header.component";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { ModuleType } from "@enums/ModuleType";
import {
    TranslatePipe,
    injectTranslation,
    provideTranslationScope,
} from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assetmanagement/i18n/scope";
import { CommonModule } from "@angular/common";
import { CerrixSlideOutEditItemComponent } from "@app/shared/organisms/cerrix-slide-out-edit-item/cerrix-slide-out-edit-item.component";
import { CerrixSlideOutEditItemMenuItems } from "@app/shared/organisms/cerrix-slide-out-edit-item/models/cerrix-slide-out-edit-item-menu-items.model";
import { EditAssetGeneralInformationComponent } from "@app/assetmanagement/components/edit-asset-general-information/edit-asset-general-information.component";
import { EditAssetOwnershipInformationComponent } from "@app/assetmanagement/components/edit-asset-ownership-information/edit-asset-ownership-information.component";
import { AssetUpdatePermissionsModel } from "@models/assetmanagement/AssetUpdatePermissionsModel";
import { EditAssetLifecycleInformationComponent } from "@app/assetmanagement/components/edit-asset-lifecycle-information/edit-asset-lifecycle-information.component";
import { ThirdPartyDataService } from "@pages/thirdparty/services/ThirdPartyDataService";
import { ThirdPartyContractDataService } from "@pages/thirdparty/services/ThirdPartyContractDataService";
import { firstValueFrom } from "rxjs";
import { MatMenuModule } from "@angular/material/menu";
import { AssetTabsHeaderComponent } from "@app/assetmanagement/components/asset-tabs-header/asset-tabs-header.component";
import { AssetDocumentHyperlinkToggleComponent } from "@app/assetmanagement/components/asset-document-hyperlink-toggle/asset-document-hyperlink-toggle.component";
import { IdGuidNameCombination } from "@models/generic/IdGuidNameCombination";
import { HttpClient } from "@angular/common/http";
import { EditAssetProcessInformationComponent } from "@app/assetmanagement/components/edit-asset-process-information/edit-asset-process-information.component";
import { ProcessInformationModel } from "@models/assetmanagement/processinformationmodel";
import { CerrixUserSelectComponent } from "@app/shared/organisms/cerrix-user-select/cerrix-user-select.component";
import { CerrixUserTypePickerConfig } from "@app/shared/organisms/cerrix-user-type-picker/models/cerrix-user-type-picker-config";
import { AssetLinkedItemsSectionComponent } from "@app/assetmanagement/components/asset-linked-items-section/asset-linked-items-section.component";

@Component({
    selector: "app-assetmanagement-detail-view",
    templateUrl: "./assetmanagement-detail-view.component.html",
    styleUrls: ["./assetmanagement-detail-view.component.scss"],
    imports: [
        CommonModule,
        CerrixPageFooterComponent,
        CerrixButtonComponent,
        CerrixIconButtonComponent,
        CerrixHeaderComponent,
        CerrixSlideOutEditItemComponent,
        MenuSystemDirective,
        TranslatePipe,
        EditAssetGeneralInformationComponent,
        EditAssetLifecycleInformationComponent,
        EditAssetOwnershipInformationComponent,
        EditAssetProcessInformationComponent,
        MatTabsModule,
        MatMenuModule,
        AssetTabsHeaderComponent,
        AssetDocumentHyperlinkToggleComponent,
        CerrixUserSelectComponent,
        AssetLinkedItemsSectionComponent,
    ],
    viewProviders: [provideTranslationScope(scope)],
    providers: [CerrixInputValidationRegistryService],
    encapsulation: ViewEncapsulation.None,
})
export class AssetManagementDetailViewComponent implements OnInit {
    tab: TabModel;
    id: string;

    protected model: AssetManagementModel;

    protected breadcrumbs: string[] = [];

    protected canEdit = true;
    protected selectedTab = "overview";

    public organizationOwnerList: { label: string; value: string }[];
    protected businessDimensionList: { label: string; value: string }[] = [];

    protected assignedTypes: CerrixUserTypePickerConfig[] = [];
    protected assignedValue: Record<string, string[]> = {};

    protected editActive = false;

    protected thirdPartyName: string | null = null;
    protected contractNumber: string | null = null;
    protected users: IdGuidNameCombination[] = [];

    private updatePermissions: AssetUpdatePermissionsModel;

    private _ds = inject(AssetManagementService);
    private _translation = injectTranslation(scope);
    private _standingDataDS = inject(StandingdataDataService);
    private _thirdPartyDs = inject(ThirdPartyDataService);
    private _contractDs = inject(ThirdPartyContractDataService);
    private _http = inject(HttpClient);

    protected sections: CerrixSlideOutEditItemMenuItems;

    async ngOnInit() {
        if (!this.id) {
            this.tab.close();
            return;
        }

        // t() needs the scope loaded first; before ready() it throws in development.
        await this._translation.ready();

        let asset: AssetProjection;
        try {
            await this.loadPermissions(this.id);
            asset = await firstValueFrom(this._ds.getAssetByIdVbeta(this.id));
        } catch {
            // Any load failure closes the tab, for example a 403 when the user may not view the asset.
            // The global interceptor has already shown the error, and the page cannot render without the asset.
            this.tab.close(false);
            return;
        }

        if (this.canEdit) {
            this.loadSections();
        }

        this.breadcrumbs = [this._translation.t("detail.breadcrumbWorkspace")];

        this.model = Object.assign(new AssetManagementModel(), asset, {
            thirdPartyInformation: {
                thirdPartyId: asset.thirdPartyInformation?.thirdPartyId,
                contractId: asset.thirdPartyInformation?.contractId,
            },
            ownershipInformation: {
                ownerId: asset.ownershipInformation?.ownerId,
                owningOrganizationId: asset.ownershipInformation?.owningOrganizationId,
            },
            processInformation: Object.assign(
                new ProcessInformationModel(),
                asset.processInformation,
            ),
        });
        this.breadcrumbs.push(this.model.reference);

        await this.loadLookups();

        await Promise.all([this.loadThirdPartyName(), this.loadContractNumber()]);

        this.tab.showLoader = false;
    }

    private async loadLookups(): Promise<void> {
        const processInformation = this.model.processInformation;

        const [organizations, businessDimensions, users] = await Promise.all([
            firstValueFrom(
                this._standingDataDS.getOrganizationsIncludingSelected(
                    ModuleType.AssetManagement,
                    [
                        this.model.ownershipInformation.owningOrganizationId,
                        ...processInformation.organizationsInUse,
                    ],
                    false,
                ),
            ),
            firstValueFrom(
                this._standingDataDS.getUserViewableBusinessDimensions(
                    ModuleType.AssetManagement,
                    processInformation.businessDimensions,
                ),
            ),
            this.getUsers(),
        ]);

        this.organizationOwnerList = flattenOrgTree(organizations);
        this.businessDimensionList = flattenOrgTree(businessDimensions);
        this.users = users;
        this.refreshAssigned();
    }

    // Who is assigned, as the Assigned control shows it: the owner, the responsibles and the informed.
    private refreshAssigned(): void {
        const processInformation = this.model.processInformation;
        const ownerId = this.model.ownershipInformation?.ownerId;

        this.assignedTypes = [
            {
                key: "owner",
                label: this._translation.t("edit-ownership-information.ownerLabel"),
                users: this.users,
            },
            {
                key: "responsible",
                label: this._translation.t("edit-process-information.responsiblesLabel"),
                users: this.users,
                multiselect: true,
            },
            {
                key: "informed",
                label: this._translation.t("edit-process-information.informedLabel"),
                users: this.users,
                multiselect: true,
            },
        ];
        this.assignedValue = {
            owner: ownerId ? [ownerId] : [],
            responsible: processInformation?.responsibles ?? [],
            informed: processInformation?.informed ?? [],
        };
    }

    private async getUsers(): Promise<IdGuidNameCombination[]> {
        const processInformation = this.model.processInformation;
        const selected = [
            this.model.ownershipInformation.ownerId,
            ...processInformation.responsibles,
            ...processInformation.informed,
        ].filter(Boolean);

        if (!selected.length) {
            return [];
        }

        const params = `?selected=${selected.join(",")}`;

        return await firstValueFrom(
            this._http.get<IdGuidNameCombination[]>(`/api/users/selected` + params),
        );
    }

    protected async onHyperlinksChanged(updated: AssetManagementModel): Promise<void> {
        this.model = updated;
    }

    public edit(): void {
        if (this.canEdit) {
            this.editActive = true;
        }
    }

    /** Opens the edit slideout on the given section, as the edit icon next to a field does. */
    protected editSection(section: string): void {
        this.sections = { ...this.sections, activeItem: section };
        this.edit();
    }

    /** The icon of a field's edit button: none when the user may not edit the field's section. */
    protected editIconFor(section: string): string | undefined {
        return this.sections?.items.some((item) => item.id === section) ? "edit" : undefined;
    }

    public closeCancel(): void {
        this.editActive = false;
    }

    public async onModelChange(updated: AssetManagementModel): Promise<void> {
        const prevThirdPartyId = this.model?.thirdPartyInformation?.thirdPartyId;
        const prevContractId = this.model?.thirdPartyInformation?.contractId;
        this.model = updated;
        if (updated.thirdPartyInformation?.thirdPartyId !== prevThirdPartyId) {
            await this.loadThirdPartyName();
        }
        if (updated.thirdPartyInformation?.contractId !== prevContractId) {
            await this.loadContractNumber();
        }
    }

    protected async onOwnershipChange(updated: AssetManagementModel): Promise<void> {
        this.model = updated;
        await this.loadLookups();
    }

    protected async onProcessInformationChange(updated: AssetManagementModel): Promise<void> {
        this.model = updated;
        await this.loadLookups();
    }

    protected organizationsInUseNames(): string {
        return this.namesOf(
            this.model.processInformation.organizationsInUse,
            this.organizationOwnerList,
        );
    }

    protected businessDimensionNames(): string {
        return this.namesOf(
            this.model.processInformation.businessDimensions,
            this.businessDimensionList,
        );
    }

    // An id that is not in the list belongs to something the user may not see, so it is left out.
    private namesOf(ids: string[], list: { label: string; value: string }[]): string {
        return ids
            .map((id) => list.find((item) => item.value === id)?.label)
            .filter(Boolean)
            .join(", ");
    }

    public getOrgOwnerName(orgOwnerGuid: string): string {
        return this.organizationOwnerList.find((org) => org.value === orgOwnerGuid)?.label;
    }

    private async loadThirdPartyName(): Promise<void> {
        if (!this.model.thirdPartyInformation?.thirdPartyId) {
            this.thirdPartyName = null;
            return;
        }
        try {
            const thirdPartyMetadata = await firstValueFrom(
                this._thirdPartyDs.getThirdPartiesMetaData([
                    this.model.thirdPartyInformation.thirdPartyId,
                ]),
            );
            const tp = thirdPartyMetadata?.find(
                (tp) => tp.id === this.model.thirdPartyInformation.thirdPartyId,
            );

            this.thirdPartyName = tp ? (toDisplayValue(tp.name, tp.archived) ?? null) : null;
        } catch {
            this.thirdPartyName = null;
        }
    }

    private async loadContractNumber(): Promise<void> {
        if (!this.model.thirdPartyInformation?.contractId) {
            this.contractNumber = null;
            return;
        }
        try {
            const idsToInclude = this.model.thirdPartyInformation?.contractId
                ? [this.model.thirdPartyInformation?.contractId]
                : [];
            const contracts = await firstValueFrom(
                this._contractDs.getContractsMetadata(
                    this.model.thirdPartyInformation.thirdPartyId,
                    idsToInclude,
                ),
            );
            const contract = contracts?.find(
                (c) => c.id === this.model.thirdPartyInformation.contractId,
            );
            this.contractNumber = contract
                ? toDisplayValue(contract.name, contract.archived)
                : null;
        } catch {
            this.contractNumber = null;
        }
    }

    private async loadPermissions(id: string): Promise<void> {
        const permissions = await firstValueFrom(this._ds.getPermissionsVbeta(id));
        this.updatePermissions = new AssetUpdatePermissionsModel(permissions.permissions);
        this.canEdit = permissions.permissions.length !== 0;
    }

    private loadSections(): void {
        const sections: CerrixSlideOutEditItemMenuItems = {
            items: [],
            activeItem: "general",
        };

        if (this.updatePermissions.canUpdateMetadata) {
            const metadataSection = {
                name: this._translation.t("edit-general-information.title"),
                id: "general",
            };
            sections.items.push(metadataSection);
        }

        if (this.updatePermissions.canUpdateLifecycle) {
            const metadataSection = {
                name: this._translation.t("edit-lifecycle-information.title"),
                id: "lifecycle",
            };
            sections.items.push(metadataSection);
        }

        if (this.updatePermissions.canUpdateOwnership) {
            const ownershipSection = {
                name: this._translation.t("edit-ownership-information.title"),
                id: "ownership",
            };
            sections.items.push(ownershipSection);
        }

        if (this.updatePermissions.canUpdateProcessInformation) {
            const processInformationSection = {
                name: this._translation.t("edit-process-information.title"),
                id: "processInformation",
            };
            sections.items.push(processInformationSection);
        }

        sections.activeItem = sections.items.first().id;

        this.sections = sections;
    }
}
